import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";

export type OrderStatus = "pending" | "processing" | "completed" | "cancelled";
export type PaymentStatus = "pending" | "paid" | "failed" | "refunded";
export type PaymentMethod = "wallet" | "card" | "crypto";

export interface AdminOrderItem {
  productId?: string | { _id: string; name?: string; thumbnailUrl?: string; category?: string };
  name: string;
  thumbnailUrl?: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface AdminOrderUser {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface AdminOrder {
  _id?: string;
  orderNumber?: string;
  userId?: AdminOrderUser | string;
  items?: AdminOrderItem[];
  subtotal?: number;
  platformFee?: number;
  totalAmount?: number;
  currency?: string;
  paymentMethod?: PaymentMethod;
  paymentProvider?: string | null;
  paymentRef?: string | null;
  paymentStatus?: PaymentStatus;
  status?: OrderStatus;
  cancelledAt?: string;
  cancellationReason?: string;
  activationTimeline?: Array<{ event: string; label: string; timestamp: string; completed: boolean }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface OrderFilters {
  page?: number;
  limit?: number;
  status?: OrderStatus;
  paymentStatus?: PaymentStatus;
  search?: string;
}

interface OrderListResponse {
  success?: boolean;
  data?: AdminOrder[];
  pagination?: { page: number; limit: number; total: number; pages: number };
}

interface OrderOneResponse {
  success?: boolean;
  message?: string;
  data?: AdminOrder;
}

export const getAdminOrders = async (
  filters: OrderFilters = {}
): Promise<{ orders: AdminOrder[]; pagination: OrderListResponse["pagination"] | null }> => {
  const params = new URLSearchParams();
  if (filters.page) params.append("page", String(filters.page));
  if (filters.limit) params.append("limit", String(filters.limit));
  if (filters.status) params.append("status", filters.status);
  if (filters.paymentStatus) params.append("paymentStatus", filters.paymentStatus);
  if (filters.search) params.append("search", filters.search);
  const qs = params.toString();
  const url = qs ? `${ENDPOINTS.ADMIN.ORDERS}?${qs}` : ENDPOINTS.ADMIN.ORDERS;
  const res = await apiClient.get<OrderListResponse>(url);
  return { orders: res.data?.data || [], pagination: res.data?.pagination || null };
};

export const getAdminOrder = async (id: string): Promise<AdminOrder | null> => {
  const res = await apiClient.get<OrderOneResponse>(ENDPOINTS.ADMIN.ORDER_DETAIL(id));
  return res.data?.data || null;
};

export const updateOrderStatus = async (
  id: string,
  status: OrderStatus,
  reason?: string
): Promise<AdminOrder | null> => {
  const res = await apiClient.patch<OrderOneResponse>(ENDPOINTS.ADMIN.ORDER_STATUS(id), { status, reason });
  return res.data?.data || null;
};

export const updatePaymentStatus = async (
  id: string,
  paymentStatus: PaymentStatus
): Promise<AdminOrder | null> => {
  const res = await apiClient.patch<OrderOneResponse>(ENDPOINTS.ADMIN.ORDER_PAYMENT_STATUS(id), { paymentStatus });
  return res.data?.data || null;
};
